import type { MarketplaceOpportunityListItemDto } from '@foodfluence/shared';
import type { ExploreSort, ExploreTab, SectionsData } from './utils';

/** Explore's loaded state, kept across route switches so coming back doesn't refetch or reset. */
export type ExploreCacheEntry = {
  key: string;
  savedAt: number;
  sectionsData: SectionsData;
  opportunities: MarketplaceOpportunityListItemDto[];
  tab: ExploreTab;
  ugcOnly: boolean;
  sort: ExploreSort;
  brandsExpanded: boolean;
  scrollY: number;
};

type KeyFilters = {
  searchQuery?: string;
  location?: { lat: number; lng: number; radius?: number } | null;
  searchRadius?: number;
  creatorTypes?: string[];
};

export const EXPLORE_CACHE_TTL_MS = 10 * 60 * 1000;

// ponytail: single in-memory entry (one Explore page, one user); lost on full reload, which is fine.
let entry: ExploreCacheEntry | null = null;

/** Everything that changes what the explore query returns. */
export const exploreCacheKey = (userId: string | undefined, f: KeyFilters): string =>
  JSON.stringify([
    userId ?? '',
    f.searchQuery ?? '',
    f.location ? [f.location.lat, f.location.lng, f.location.radius ?? null] : null,
    f.searchRadius ?? null,
    [...(f.creatorTypes ?? [])].sort(),
  ]);

/** The cached entry for `key`, or null if there is none, it belongs to other inputs, or it is stale. */
export const readExploreCache = (key: string, now: number = Date.now()): ExploreCacheEntry | null =>
  entry && entry.key === key && now - entry.savedAt < EXPLORE_CACHE_TTL_MS ? entry : null;

/** Stores a snapshot; in-flight page loads are recorded as settled so pagination resumes after hydrate. */
export const writeExploreCache = (next: Omit<ExploreCacheEntry, 'savedAt'>, now: number = Date.now()): void => {
  const sectionsData = Object.fromEntries(
    Object.entries(next.sectionsData).map(([section, value]) => [
      section,
      { ...value, pagination: { ...value.pagination, loading: false } },
    ])
  ) as SectionsData;
  entry = { ...next, sectionsData, savedAt: now };
};

/** Keeps the entry but updates the remembered scroll position. */
export const saveExploreScroll = (key: string, scrollY: number): void => {
  if (entry && entry.key === key) entry = { ...entry, scrollY };
};

export const clearExploreCache = (): void => {
  entry = null;
};
